import { Gender } from '../general';
import { Allergens, Ingredient } from './ingredient';
import INutritionalInformation from './INutritionalInformation';
import { MealIngredient, Unit } from './mealingredient';
import { MealIngredientCount } from './mealIngredientCount';
import { MealIngredientData } from './mealIngredientData';
import { DietGoal, FoodPreference, MealOfDay } from './mealplan';

export const portionReductionPercentageForNonActiveUsers = 17;

// Will have Couchdb DB document
export class Meal implements INutritionalInformation {
  public static OldIngredientCalories(meal: Meal): number {
    if (meal.ingredients != null && meal.ingredients.length > 0) {
      return Ingredient.SumCalories(meal.ingredients);
    }
    return 0;
  }

  public static Calories(meal: Meal, allmealIngredients: MealIngredient[]): number {
    return MealIngredient.SumCalories(this.getmealIngredientsData(meal, allmealIngredients));
  }

  public static Fat(meal: Meal, allmealIngredients: MealIngredient[], useImperialUnit = false): number {
    return MealIngredient.SumFat(this.getmealIngredientsData(meal, allmealIngredients), useImperialUnit);
  }

  public static Carbs(meal: Meal, allmealIngredients: MealIngredient[], useImperialUnit = false): number {
    return MealIngredient.SumCarbs(this.getmealIngredientsData(meal, allmealIngredients), useImperialUnit);
  }

  public static Protein(meal: Meal, allmealIngredients: MealIngredient[], useImperialUnit = false): number {
    return MealIngredient.SumProtein(this.getmealIngredientsData(meal, allmealIngredients), useImperialUnit);
  }

  public static GenerateId(displayName: string, mealPlanId: string): string {
    return `${displayName}-${mealPlanId}`;
  }

  public static isLegacyMeal(meal: Meal): boolean {
    // check if there are legacy ingredients
    return meal.ingredients != null && meal.ingredients.length > 0;
  }

  public static setIngredientDescriptions(meal: Meal, allMealIngredients: MealIngredient[],
                                          allergens: Allergens[],useImperialUnit = false) {
    if (allMealIngredients == null || meal.mealIngredients == null) {
      return;
    }
    const userAllergens = allergens ?? [];
    for (const mealIngredient of meal.mealIngredients) {
      const ingredientDetails = allMealIngredients.find((i) => i._id === mealIngredient.ingredientId);
      if (ingredientDetails != null) {
        // set on a separate nested mealIngredient field, because setIngredientDescriptions is called multiple
        // times (causing imperial convertion to occur multiple times)
        // TODO: figure out why setIngredientDescriptions is called multiple times, might be recipes + shopping
        // built together or some caching
        mealIngredient.displayData = MealIngredient
          .getMealIngredientsCount(mealIngredient.quantity, ingredientDetails, useImperialUnit, userAllergens);
      }
    }
  }

  public static setLowerCaloricMealQuantities(meal: Meal, allIngredients: MealIngredient[]) {
    meal.mealIngredients = meal.mealIngredients.map(
      (ingredient) => {
        const mealIngredient = allIngredients.find(
          ({ _id }) => _id === ingredient.ingredientId,
        );
        if (
          mealIngredient?.unit === Unit.Unit ||
          mealIngredient?.unit === Unit.TeaSpoon
        ) {
          return ingredient;
        }
        return {
          ...ingredient,
          quantity: Math.max(
            1,
            Math.round((ingredient.quantity * (100 - portionReductionPercentageForNonActiveUsers)) / 100),
          ),
        };
      },
    );
  }

  public static setImageUrls(meal: Meal) {
    const cloudinaryRoot = 'https://res.cloudinary.com/fitbeat-operations/image/upload';

    const mealIdEscapedForCloudinary = meal._id.replace(/ /g, '_');
    if (Meal.isLegacyMeal(meal)) {
      const legacyFolder = 'OldMeals';

      const legacyThumbTransform = 't_MealThumbnailSmall';
      meal.urlForThumbImage
        = `${cloudinaryRoot}/${legacyThumbTransform}/${legacyFolder}/${mealIdEscapedForCloudinary}`;
      const legacyMobileTransform = 't_MealMobileMedium';
      meal.urlForMobileImage
        = `${cloudinaryRoot}/${legacyMobileTransform}/${legacyFolder}/${mealIdEscapedForCloudinary}`;
      const legacyDesktopTransform = 't_MealDesktopMedium';
      meal.urlForDesktopImage
        = `${cloudinaryRoot}/${legacyDesktopTransform}/${legacyFolder}/${mealIdEscapedForCloudinary}`;
    } else {
      const currentFolder = 'Meals';

      const thumbTransform = 't_MealThumbSmall4';
      meal.urlForThumbImage
        = `${cloudinaryRoot}/${thumbTransform}/${currentFolder}/${mealIdEscapedForCloudinary}-thumb.jpg`;
      const mobileTransform = 't_MealMobileMed4';

      // note that we use the thumb image for mobile now,
      // since it works better not being as high as the mobile image
      meal.urlForMobileImage
        = `${cloudinaryRoot}/${mobileTransform}/${currentFolder}/${mealIdEscapedForCloudinary}-thumb.jpg`;
      const desktopTransform = 't_MealDesktopWide4';
      meal.urlForDesktopImage
        = `${cloudinaryRoot}/${desktopTransform}/${currentFolder}/${mealIdEscapedForCloudinary}-desktop.jpg`;
    }
  }

  private static getmealIngredientsData(meal: Meal, allmealIngredients: MealIngredient[]): MealIngredientData[] {
    const mealIngredientsData: MealIngredientData[] = [];
    if (meal.mealIngredients === undefined || meal.mealIngredients.length === 0) {
      return mealIngredientsData;
    }
    meal.mealIngredients.forEach((mealIngredient) => {
      const ingredient = allmealIngredients.find((element) => {
        return element._id === mealIngredient.ingredientId;
      });
      if (ingredient instanceof MealIngredient) {
        const mealIngredientData = new MealIngredientData();
        mealIngredientData.ingredient = ingredient;
        mealIngredientData.quantity = mealIngredient.quantity;
        mealIngredientsData.push(mealIngredientData);
      }
    });
    return mealIngredientsData;
  }

  // tslint:disable-next-line:variable-name
  public _id: string;
  // meal plan id is set, since a similar but not identical meals can be found on different plans
  public mealPlanId: string;
  public displayName: string;
  public thumbnail: string;
  public image: string;
  public mealIngredients: MealIngredientCount[];
  public ingredients: Ingredient[];
  public instructions: string[];
  public tips: string[];
  public timeOfMeal: MealOfDay[];
  public foodPreference: FoodPreference[];
  public dietGoal: DietGoal;
  public gender: Gender;
  public isInShoppingList: boolean;

  // transient urls
  public urlForThumbImage: string;
  public urlForDesktopImage: string;
  public urlForMobileImage: string;

  // transient properties
  public calories: number;
  public fat: number;
  public carbs: number;
  public protein: number;
  public prepDuration: number;
  public approveStatus: string;
  public organisation?: string;
  public excludedOrganisations?: string[];
}
